import { Box, Grid, MenuItem, Stack, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { usageClient } from '../../api/client';
import { QueryUsageEvent, QueryUsageOrgRow, QueryUsageUserRow } from '../../api/schema';
import { CopyTsvButton } from '../../component/copyTsvButton/CopyTsvButton';
import KPI from '../../component/kpi/KPI';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import Table from '../../component/table/Table';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { listOrganizations, selectAllOrganizations } from '../../redux/slices/organizations/organizationsSlice';
import {
  getUsageEvents,
  getUsageSummary,
  getUsageSummaryData,
  listUsage,
  usageEventsFetching,
  usageSummaryFetching,
} from '../../redux/slices/usage/usageSlice';
import { useDispatch } from '../../redux/store';
import { Card, CardHeaderTabs } from '../../styled/components';
import { HORIZONS, Horizon, horizonSince } from './usageHorizon';

type UserType = 'all' | 'admin' | 'customer';
type TabKey = 'summary' | 'details';

// Rows the details copy fetches at most; the API caps a page at this too.
const EXPORT_LIMIT = 5000;

const USER_TYPES: { key: UserType; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'admin', label: 'Admin' },
  { key: 'customer', label: 'Customer' },
];

const when = (iso?: string | null): string => (iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_SHORT) : '');
const place = (row: QueryUsageEvent): string => [row.city, row.region, row.country].filter(Boolean).join(', ');

// Admin usage dashboard: who has been using the app, from where, and on which
// pages. The horizon is computed in the viewer's own calendar.
export const Usage = (): ReactElement => {
  const dispatch = useDispatch();
  const [params, setURLParams] = useSearchParams();
  const [horizon, setHorizon] = useState<Horizon>('mtd');
  const [userType, setUserType] = useState<UserType>('all');
  const [tab, setTab] = useState<TabKey>('summary');
  const [search, setSearch] = useState('');
  const [organizationId, setOrganizationId] = useState('');

  // Every active organization, for the filter.
  const orgQuery = useMemo(() => ({ active: 'true', offset: 0, limit: 10000, full: 'yes' }), []);
  const organizations = useSelector(selectAllOrganizations(orgQuery));
  useEffect(() => {
    dispatch(listOrganizations(orgQuery));
  }, [dispatch, orgQuery]);
  const orgOptions = useMemo(() => [...(organizations.result ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')), [organizations.result]);

  const since = useMemo(() => horizonSince(horizon), [horizon]);
  const summary = useSelector(getUsageSummaryData);
  const summaryFetching = useSelector(usageSummaryFetching);

  useEffect(() => {
    dispatch(getUsageSummary({ since, userType, organizationId: organizationId || undefined }));
  }, [dispatch, since, userType, organizationId]);

  // Details tab: server-paged and server-sorted through the shared table.
  const listQuery = useMemo(
    () => ({ since, userType, ...(organizationId ? { organizationId } : {}), ...(params.search ? { search: String(params.search) } : {}) }),
    [since, userType, organizationId, params.search],
  );
  const events = useSelector(getUsageEvents(listQuery));
  const eventsFetching = useSelector(usageEventsFetching);
  const [pagination, paginationToGet, paginationControl] = useTablePagination({ paginationDirty: events.pagination, defaultLimit: 50 });

  useEffect(() => {
    if (tab === 'details') {
      dispatch(listUsage({ ...listQuery, ...paginationToGet }));
    }
  }, [dispatch, tab, listQuery, paginationToGet]);

  const applySearch = useCallback(() => setURLParams({ newParams: { search: search.trim() || undefined, offset: '' } }), [search, setURLParams]);

  const orgColumns = useMemo(
    () => [
      { Header: 'Organization', accessor: 'organization_name' },
      { Header: 'Type', accessor: 'org_type' },
      { Header: 'Active Users', accessor: 'active_users', align: 'right' },
      { Header: 'Total Views', accessor: 'total_views', align: 'right' },
      { Header: 'Top Pages', accessor: 'top_pages', ellipsis: true, minWidth: 260 },
      { Header: 'First Access', accessor: 'first_access', Cell: ({ value }: { value?: string }) => when(value) },
      { Header: 'Last Active', accessor: 'last_access', Cell: ({ value }: { value?: string }) => when(value) },
    ],
    [],
  );
  const userColumns = useMemo(
    () => [
      { Header: 'User', accessor: 'name' },
      { Header: 'Email', accessor: 'email' },
      { Header: 'Organization', accessor: 'organization_name' },
      { Header: 'Role', accessor: 'roles' },
      { Header: 'Total Views', accessor: 'total_views', align: 'right' },
      { Header: 'Top 5 Pages', accessor: 'top_pages', ellipsis: true, minWidth: 260 },
      { Header: 'First Access', accessor: 'first_access', Cell: ({ value }: { value?: string }) => when(value) },
      { Header: 'Last Active', accessor: 'last_access', Cell: ({ value }: { value?: string }) => when(value) },
    ],
    [],
  );
  const eventColumns = useMemo(
    () => [
      { Header: 'User', accessor: 'name', sortBy: 'name' },
      { Header: 'Email', accessor: 'email', sortBy: 'email' },
      { Header: 'Organization', accessor: 'organization_name', sortBy: 'organization_name' },
      { Header: 'IP Address', accessor: 'ip_address', sortBy: 'ip_address' },
      { Header: 'Location', accessor: 'city', sortBy: 'city', Cell: ({ row }: { row: { original: QueryUsageEvent } }) => place(row.original) },
      { Header: 'Page', accessor: 'page', sortBy: 'page', ellipsis: true, minWidth: 200 },
      { Header: 'Timestamp', accessor: 'accessed_at', sortBy: 'accessed_at', Cell: ({ value }: { value?: string }) => when(value) },
    ],
    [],
  );

  const orgs: QueryUsageOrgRow[] = summary?.organizations ?? [];
  const users: QueryUsageUserRow[] = summary?.users ?? [];
  const orgTsv = orgs.map((o) => [o.organization_name, o.org_type, o.active_users, o.total_views, o.top_pages, when(o.first_access), when(o.last_access)]);
  const userTsv = users.map((u) => [u.name, u.email, u.organization_name, u.roles, u.total_views, u.top_pages, when(u.first_access), when(u.last_access)]);
  // The details copy takes the whole filtered log, not just the page on screen.
  const eventRow = (e: QueryUsageEvent) => [e.name, e.email, e.organization_name, e.ip_address, place(e), e.page, when(e.accessed_at)];
  const fetchAllEvents = useCallback(async () => {
    const res = await usageClient.listUsage(
      since,
      userType,
      organizationId || undefined,
      undefined,
      params.search ? String(params.search) : undefined,
      pagination.sortBy || undefined,
      0,
      EXPORT_LIMIT,
    );
    return (res.data.result ?? []).map(eventRow);
  }, [since, userType, organizationId, params.search, pagination.sortBy]);

  return (
    <>
      {(summaryFetching || (tab === 'details' && eventsFetching)) && <LinearProgress />}
      <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1} alignItems="center" sx={{ mb: 4 }}>
        <ToggleButtonGroup size="small" exclusive value={horizon} onChange={(_e, v: Horizon | null) => v && setHorizon(v)}>
          {HORIZONS.map((h) => (
            <ToggleButton key={h.key} value={h.key}>
              {h.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField select size="small" label="User Type" value={userType} onChange={(e) => setUserType(e.target.value as UserType)} sx={{ minWidth: 140 }}>
          {USER_TYPES.map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Organization" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)} sx={{ minWidth: 240 }}>
          <MenuItem value="">All organizations</MenuItem>
          {orgOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <KPI title="Users" data={[{ amount: summary?.kpis?.users, label: 'active', color: 'blue' }]} fetching={summaryFetching} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KPI title="Companies" data={[{ amount: summary?.kpis?.orgs, label: 'active', color: 'blue' }]} fetching={summaryFetching} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KPI title="Pages" data={[{ amount: summary?.kpis?.pages, label: 'distinct', color: 'blue' }]} fetching={summaryFetching} />
        </Grid>
      </Grid>

      <Card sx={{ mb: 4 }}>
        <CardHeaderTabs
          title={
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tab} onChange={(_e, v: TabKey) => setTab(v)}>
                <Tab label="Summary" value="summary" />
                <Tab label="Usage Details" value="details" />
              </Tabs>
            </Box>
          }
        />
        {tab === 'summary' ? (
          <Box sx={{ p: 2 }}>
            <Table
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6">Organizations ({orgs.length})</Typography>
                  <CopyTsvButton
                    label="Organization summary"
                    header={['Organization', 'Type', 'Active Users', 'Total Views', 'Top Pages', 'First Access', 'Last Active']}
                    rows={orgTsv}
                  />
                </Stack>
              }
              columns={orgColumns}
              data={orgs}
              size="small"
              withPagination={false}
            />
            <Box sx={{ mt: 4 }}>
              <Table
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">Users ({users.length})</Typography>
                    <CopyTsvButton
                      label="User summary"
                      header={['User', 'Email', 'Organization', 'Role', 'Total Views', 'Top 5 Pages', 'First Access', 'Last Active']}
                      rows={userTsv}
                    />
                  </Stack>
                }
                columns={userColumns}
                data={users}
                size="small"
                withPagination={false}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                size="small"
                label="Search user or page"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                onBlur={applySearch}
                sx={{ minWidth: 260 }}
              />
              <CopyTsvButton
                label="Usage details"
                header={['User', 'Email', 'Organization', 'IP Address', 'Location', 'Page', 'Timestamp']}
                getRows={fetchAllEvents}
              />
            </Stack>
            <Table columns={eventColumns} data={events.result ?? []} size="small" pagination={pagination} paginationControl={paginationControl} />
          </Box>
        )}
      </Card>
    </>
  );
};

export default Usage;
